import {
  cancelChatConfigQueryOptions,
  changeFrequencyStayAiSubscription,
  contactSupportStayAiSubscription,
  extendPauseStayAiSubscription,
  fetchProductsToRate,
  getOrderNowStayAiSubscription,
  pauseStayAiSubscription,
  setNextBillingDateStayAiSubscription,
  skipNextOrderStayAiSubscription,
  submitCancelRequest,
  submitScheduleCancelAfterNextDelivery,
  submitSwitchToOneTime,
  submitUndoScheduledCancel,
  swapStatusQueryOptions,
  unpauseStayAiSubscription,
  type CancelChatOfferContext,
  type StayAiIntervalUnit,
  type SwitchToOneTimeResult,
} from "@/lib/api";
import {
  setCancelFlowVariant,
  trackBuyAgainWidgetInteract,
  type CancelFlowVariant,
  type SubscriptionRatingActionType,
} from "@/lib/tracking";
import {
  getStickyCancelFlowAssignment,
  readStoredCancelFlowAssignment,
} from "./cancel-flow-assignment";
import {
  cancelFlowTrackingProps,
  leverActionTrackingProps,
  resolveLeverSource,
} from "./cancel-attempt-id";
import { hasCancelChatOverride } from "./cancel-chat-override";
import { hasCancelAfterDeliveryOverride } from "./cancel-after-delivery-override";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ActionResultDialog } from "./ActionResultDialog";
import { X } from "lucide-react";
import { useEffect, useRef, useState } from "preact/hooks";
import { Button } from "../ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import { ContactSupportDialog } from "./ContactSupportDialog";
import { ExtendPauseDialog } from "./ExtendPauseDialog";
import { DARK_BRAND_BTN_CLASS, PRIMARY_BRAND_BTN_CLASS } from "./constants";
import { ManageSubscriptionProductSummary } from "./ManageSubscriptionProductSummary";
import {
  ManageSubscriptionSheetFooter,
  type SubscriptionFooterAction,
} from "./ManageSubscriptionSheetFooter";
import {
  addBillingInterval,
  extendPauseByMonths,
  getAgeBasedDeliveryDateBounds,
  getAgeBasedMaxDate,
  getAllowedExtendPauseMonths,
  getAllowedPauseMonths,
  getDelayPresets,
  pauseUntilMonthsFromToday,
} from "./pause-dates";
import { PauseSubscriptionDialog } from "./PauseSubscriptionDialog";

import {
  CancelSubscriptionDialog,
  type CancelAction,
  type CancelInvoicePayment,
  type CancelStep,
} from "./CancelSubscriptionDialog";
import { CancelSubscriptionChat } from "./CancelSubscriptionChat";
import { SwapPendingBanner } from "./SwapPendingBanner";
import { ScheduledCancelBanner } from "./ScheduledCancelBanner";
import { CancelTodayDialog } from "./CancelTodayDialog";
import { cancelTodayLabel, shouldCloseSheetAfterCancelFlow } from "./cancel-after-delivery-copy";
import { applyScheduledGate, isCancelScheduled } from "./cancel-scheduled-state";
import { DelayDialog } from "./DelayDialog";
import { ChangeFrequencyDialog } from "./ChangeFrequencyDialog";
import { GetItNowDialog } from "./GetItNowDialog";
import { SkipNextOrderDialog } from "./SkipNextOrderDialog";
import { toastSubscriptionApiError } from "./subscription-errors";
import { formatSubscriptionDateForDisplay } from "./formatters";
import type { ManageSubscriptionSheetProps } from "./types";
import { UnpauseSubscriptionDialog } from "./UnpauseSubscriptionDialog";

/**
 * Dev-only force-on for the conversational cancellation chatbot, bypassing the
 * runtime rollout. Gated on import.meta.env.DEV so a production bundle built
 * with the flag accidentally set can never defeat the runtime kill switch. In
 * production the chat-vs-dialog split comes from the cancel-chat-config
 * endpoint (CANCEL_CHAT_ROLLOUT_PERCENT on the Next.js app) with deterministic
 * per-customer bucketing — see cancel-flow-assignment.ts — so the A/B can be
 * moved, or killed to 0, without rebuilding the widget. Internal testers force
 * the chat in production with ?so_cancel_chat=1 (see cancel-chat-override.ts).
 */
const FORCE_CANCEL_CHAT =
  import.meta.env.DEV && import.meta.env.VITE_CANCEL_CHATBOT === "true";

export function ManageSubscriptionSheet({
  product,
  products,
  open,
  onOpenChange,
  trackingShop,
  customerEmail,
  customerName,
  shopifyCustomerId,
  initialAction,
}: ManageSubscriptionSheetProps) {
  const isGetItNowOnly = initialAction === "get-it-now";
  const isSkipOnly = initialAction === "skip-next-order";
  const isPauseOnly = initialAction === "pause-subscription";
  const isInitialActionOnly = initialAction != null;
  const queryClient = useQueryClient();
  const onOpenChangeRef = useRef(onOpenChange);
  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  }, [onOpenChange]);

  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  // Hard-failure fallback: if the AI cancel chat errors, drop to the old dialog.
  const [chatFailed, setChatFailed] = useState(false);

  // Chat-vs-dialog assignment, sticky for the tab session: the first resolved
  // assignment is stored and reused (readStoredCancelFlowAssignment), so the
  // surface can never flip between two opens and later opens resolve
  // synchronously without waiting for the config fetch. NULL only until the
  // config resolves on the session's very first open: the customer is shown
  // the dialog meanwhile, but events are stamped "unassigned" — never a real
  // arm — so a fast clicker can't be counted into (or across) both arms.
  // Forced sessions (dev flag / ?so_cancel_chat=1 internal testing) always get
  // the chat and stamp "forced", keeping tester traffic out of the A/B.
  const forcedCancelChat = FORCE_CANCEL_CHAT || hasCancelChatOverride();
  const cancelChatConfigQuery = useQuery(cancelChatConfigQueryOptions());
  const cancelAfterDeliveryEnabled =
    (cancelChatConfigQuery.data?.cancelAfterDeliveryEnabled ?? false) ||
    hasCancelAfterDeliveryOverride();
  const assignedCancelFlow: CancelFlowVariant | null = forcedCancelChat
    ? "chat"
    : cancelChatConfigQuery.data
      ? getStickyCancelFlowAssignment(
          shopifyCustomerId != null
            ? String(shopifyCustomerId)
            : (customerEmail ?? null),
          cancelChatConfigQuery.data.percentage,
        )
      : readStoredCancelFlowAssignment();
  // Frozen while the cancel surface is open so a late first-open config
  // response can never swap the UI mid-conversation (stickiness makes the
  // assignment immutable afterwards anyway).
  const [cancelFlowVariant, setActiveCancelFlowVariant] =
    useState<CancelFlowVariant | null>(forcedCancelChat ? "chat" : null);
  useEffect(() => {
    if (!cancelDialogOpen) setActiveCancelFlowVariant(assignedCancelFlow);
  }, [assignedCancelFlow, cancelDialogOpen]);
  // Stamp the register synchronously during render — NOT in an effect — so it
  // is already set when the manage-open tracking effect below fires on first
  // mount (an effect reading the frozen state would still see null then). The
  // register is a module-level value and this call is idempotent, so setting
  // it during render is safe. Intent-to-treat: stamps the ASSIGNMENT, not the
  // (possibly frozen) shown surface.
  setCancelFlowVariant(
    forcedCancelChat ? "forced" : (assignedCancelFlow ?? "unassigned"),
  );

  useEffect(() => {
    if (!open || !trackingShop || !product) return;
    trackBuyAgainWidgetInteract("subscription_manage_open", trackingShop, {
      productId: product.id,
      subscription_contract_id: product.shopify_subscription_id ?? undefined,
    });
  }, [open, trackingShop, product]);

  const [getItNowDialogOpen, setGetItNowDialogOpen] = useState(false);
  const [cancelTodayDialogOpen, setCancelTodayDialogOpen] = useState(false);
  const [skipNextOrderDialogOpen, setSkipNextOrderDialogOpen] = useState(false);
  const [unpauseDialogOpen, setUnpauseDialogOpen] = useState(false);
  const [delayDialogOpen, setDelayDialogOpen] = useState(false);
  // Whether Delay/Change-frequency were opened from the cancel/retention flow —
  // emphasizes the action (vs "Never mind") in that context.
  const [delayFromCancelFlow, setDelayFromCancelFlow] = useState(false);
  const [changeFrequencyFromCancelFlow, setChangeFrequencyFromCancelFlow] =
    useState(false);
  const [changeFrequencyDialogOpen, setChangeFrequencyDialogOpen] =
    useState(false);
  const [extendPauseDialogOpen, setExtendPauseDialogOpen] = useState(false);
  const [contactSupportDialogOpen, setContactSupportDialogOpen] =
    useState(false);
  const [cancelDialogInitialStep, setCancelDialogInitialStep] =
    useState<CancelStep>("why-bought");
  const [invoicePayment, setInvoicePayment] =
    useState<CancelInvoicePayment | null>(null);
  // Once an action succeeds we show a single result dialog (success message +
  // an inline rating prompt when eligible). The dialog is self-contained, so we
  // only need the headline/copy and the action type here.
  const [result, setResult] = useState<{
    actionType: SubscriptionRatingActionType;
    title: string;
    message?: string;
  } | null>(null);

  // Prefetch the products the buyer can still rate on this shop the moment the
  // sheet opens, so by the time an action completes the result dialog can
  // render the rating list immediately instead of flashing a loading state.
  const productsToRateQuery = useQuery({
    queryKey: [
      "widget-products-to-rate",
      trackingShop ?? null,
      shopifyCustomerId ?? null,
    ],
    enabled:
      open &&
      typeof trackingShop === "string" &&
      trackingShop.length > 0 &&
      shopifyCustomerId != null,
    queryFn: () => {
      if (!trackingShop || shopifyCustomerId == null) return [];
      return fetchProductsToRate({ shop: trackingShop, shopifyCustomerId });
    },
    retry: false,
    staleTime: 60_000,
  });

  // Current product-swap status — a PENDING swap (awaiting support confirmation)
  // blocks cancel. Fetched on sheet open; invalidated after a swap is accepted.
  const swapStatusQuery = useQuery({
    ...swapStatusQueryOptions(product?.shopify_subscription_id ?? 0),
    enabled: open && product != null,
  });

  const pauseMutation = useMutation({
    mutationFn: (vars: {
      subscriptionId: number;
      pausedUntil: string;
      productId: number;
      months: number;
    }) => pauseStayAiSubscription(vars.subscriptionId, vars.pausedUntil),
    onSuccess: (_data, vars) => {
      if (trackingShop) {
        trackBuyAgainWidgetInteract("subscription_pause", trackingShop, {
          productId: vars.productId,
          duration_months: vars.months,
          ...leverActionTrackingProps({
            source: "manage_tab",
            subscriptionContractId: vars.subscriptionId,
          }),
        });
      }
      void queryClient.invalidateQueries({ queryKey: ["purchased-products"] });
      void queryClient.invalidateQueries({
        queryKey: ["purchased-product-detail", vars.productId],
      });
      setResult({
        actionType: "pause",
        title: `Your subscription is paused until ${formatSubscriptionDateForDisplay(
          vars.pausedUntil,
        )}`,
      });
      setPauseDialogOpen(false);
    },
    onError: (error) => {
      toastSubscriptionApiError(error);
    },
  });

  const unpauseMutation = useMutation({
    mutationFn: (vars: {
      subscriptionId: number;
      productId: number;
      nextBillingDate: string;
    }) => unpauseStayAiSubscription(vars.subscriptionId, vars.nextBillingDate),
    onSuccess: (_data, vars) => {
      if (trackingShop) {
        trackBuyAgainWidgetInteract("subscription_unpause", trackingShop, {
          productId: vars.productId,
          subscription_contract_id: vars.subscriptionId,
        });
      }
      void queryClient.invalidateQueries({ queryKey: ["purchased-products"] });
      void queryClient.invalidateQueries({
        queryKey: ["purchased-product-detail", vars.productId],
      });
      setResult({
        actionType: "unpause",
        title: "Your subscription is now active",
      });
      setUnpauseDialogOpen(false);
    },
    onError: (error) => {
      toastSubscriptionApiError(error);
    },
  });

  const setNextBillingDateMutation = useMutation({
    mutationFn: (
      vars: {
        subscriptionId: number;
        nextBillingDate: string;
        productId: number;
        weeks: number;
        fromCancelFlow: boolean;
        orderCount?: number | null;
      } & CancelChatOfferContext,
    ) =>
      setNextBillingDateStayAiSubscription(
        vars.subscriptionId,
        vars.nextBillingDate,
        { conversationId: vars.conversationId, offerTaken: vars.offerTaken },
      ),
    onSuccess: (_data, vars) => {
      if (trackingShop) {
        const source = resolveLeverSource({
          fromCancelFlowFlag: vars.fromCancelFlow,
          conversationId: vars.conversationId,
          offerTaken: vars.offerTaken,
        });
        trackBuyAgainWidgetInteract("subscription_delay", trackingShop, {
          productId: vars.productId,
          duration_weeks: vars.weeks,
          ...leverActionTrackingProps({
            source,
            subscriptionContractId: vars.subscriptionId,
            orderCount: vars.orderCount,
            cancelAttemptId: vars.conversationId,
          }),
        });
      }
      void queryClient.invalidateQueries({ queryKey: ["purchased-products"] });
      void queryClient.invalidateQueries({
        queryKey: ["purchased-product-detail", vars.productId],
      });
      setResult({
        actionType: "change_delivery_date",
        title: `Your next delivery is now ${formatSubscriptionDateForDisplay(
          vars.nextBillingDate,
        )}`,
      });
      // Delay can be launched from the cancel/retention flow too — close both.
      setDelayDialogOpen(false);
      setDelayFromCancelFlow(false);
      setCancelDialogOpen(false);
    },
    onError: (error) => {
      toastSubscriptionApiError(error);
    },
  });

  const skipNextOrderMutation = useMutation({
    mutationFn: (vars: {
      subscriptionId: number;
      productId: number;
      shipDate: string | null;
    }) => skipNextOrderStayAiSubscription(vars.subscriptionId),
    onSuccess: (_data, vars) => {
      if (trackingShop) {
        trackBuyAgainWidgetInteract(
          "subscription_skip_next_order",
          trackingShop,
          {
            productId: vars.productId,
            subscription_contract_id: vars.subscriptionId,
          },
        );
      }
      void queryClient.invalidateQueries({ queryKey: ["purchased-products"] });
      void queryClient.invalidateQueries({
        queryKey: ["purchased-product-detail", vars.productId],
      });
      setResult({
        actionType: "skip_next_order",
        title: vars.shipDate
          ? `Your next order now ships ${formatSubscriptionDateForDisplay(
            vars.shipDate,
          )}`
          : "Your next order has been skipped",
      });
      setSkipNextOrderDialogOpen(false);
    },
    onError: (error) => {
      toastSubscriptionApiError(error);
    },
  });

  const getOrderNowMutation = useMutation({
    mutationFn: (vars: { subscriptionId: number; productId: number }) =>
      getOrderNowStayAiSubscription(vars.subscriptionId),
    onSuccess: (data, vars) => {
      if (trackingShop) {
        trackBuyAgainWidgetInteract("subscription_get_it_now", trackingShop, {
          productId: vars.productId,
          subscription_contract_id: vars.subscriptionId,
        });
      }
      if (data.redirectUrl) {
        window.location.assign(data.redirectUrl);
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ["purchased-products"] });
      void queryClient.invalidateQueries({
        queryKey: ["purchased-product-detail", vars.productId],
      });
      setResult({
        actionType: "get_it_now",
        title: "Your order is on its way",
      });
      setGetItNowDialogOpen(false);
    },
    onError: (error) => {
      toastSubscriptionApiError(error);
    },
  });

  const undoScheduledMutation = useMutation({
    mutationFn: (vars: { subscriptionId: number; productId: number }) =>
      submitUndoScheduledCancel({ subscriptionId: vars.subscriptionId }),
    onSuccess: (_data, vars) => {
      if (trackingShop) {
        trackBuyAgainWidgetInteract(
          "subscription_cancel_schedule_undone",
          trackingShop,
          {
            productId: vars.productId,
            subscription_contract_id: vars.subscriptionId,
          },
        );
      }
      void queryClient.invalidateQueries({ queryKey: ["purchased-products"] });
      void queryClient.invalidateQueries({
        queryKey: ["purchased-product-detail", vars.productId],
      });
    },
    onError: (error) => {
      toastSubscriptionApiError(error);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (vars: {
      subscriptionId: number;
      whyBought: string;
      whyDetail?: string;
      cancelReason: string;
      cancelReasonOther?: string;
      cancelReasonId?: string;
      cancelAction: CancelAction;
      productId: number;
      orderCount: number | null;
      isBeforeSecondShipment: boolean;
      offerDeclined?: boolean;
      conversationId?: string;
      cancelAttemptId?: string;
      feeAmountWaived?: number | null;
    }): Promise<SwitchToOneTimeResult | null> => {
      const meta = {
        whyBought: vars.whyBought,
        whyDetail: vars.whyDetail,
        cancelReasonId: vars.cancelReasonId,
        cancelReasonLabel: vars.cancelReason,
        cancelReasonOther: vars.cancelReasonOther,
        productId: vars.productId,
        orderCount: vars.orderCount,
        isBeforeSecondShipment: vars.isBeforeSecondShipment,
        offerDeclined: vars.offerDeclined,
        conversationId: vars.conversationId,
      };
      if (vars.cancelAction === "switch_to_one_time") {
        return submitSwitchToOneTime({
          subscriptionId: vars.subscriptionId,
          ...meta,
        });
      }
      if (vars.cancelAction === "cancel_after_next_delivery") {
        await submitScheduleCancelAfterNextDelivery({
          subscriptionId: vars.subscriptionId,
          feeAmountWaived: vars.feeAmountWaived ?? null,
          override: hasCancelAfterDeliveryOverride(),
          ...meta,
        });
        return null;
      }
      await submitCancelRequest({
        subscriptionId: vars.subscriptionId,
        ...meta,
      });
      return null;
    },
    onSuccess: (data, vars) => {
      if (trackingShop) {
        trackBuyAgainWidgetInteract(
          vars.cancelAction === "cancel_after_next_delivery"
            ? "subscription_cancel_scheduled"
            : "subscription_cancel_confirmed",
          trackingShop,
          {
            productId: vars.productId,
            subscription_contract_id: vars.subscriptionId,
            reason: vars.cancelReasonId ?? vars.cancelReason,
            cancel_action: vars.cancelAction,
            offer_declined: vars.offerDeclined ?? false,
            ...cancelFlowTrackingProps({
              cancelAttemptId: vars.cancelAttemptId ?? vars.conversationId,
              orderCount: vars.orderCount,
            }),
          },
        );
      }
      if (
        vars.cancelAction === "switch_to_one_time" &&
        data &&
        data.invoice_url &&
        data.amount != null &&
        data.currency
      ) {
        setInvoicePayment({
          invoiceUrl: data.invoice_url,
          amount: data.amount,
          currency: data.currency,
        });
        window.location.assign(data.invoice_url);
      }
      void queryClient.invalidateQueries({ queryKey: ["purchased-products"] });
      void queryClient.invalidateQueries({
        queryKey: ["purchased-product-detail", vars.productId],
      });
    },
    onError: (error) => {
      toastSubscriptionApiError(error);
    },
  });

  const changeFrequencyMutation = useMutation({
    mutationFn: (
      vars: {
        subscriptionId: number;
        interval: number;
        intervalUnit: StayAiIntervalUnit;
        productId: number;
        previousInterval: number | null;
        fromCancelFlow: boolean;
        orderCount?: number | null;
      } & CancelChatOfferContext,
    ) =>
      changeFrequencyStayAiSubscription(
        vars.subscriptionId,
        vars.interval,
        vars.intervalUnit,
        { conversationId: vars.conversationId, offerTaken: vars.offerTaken },
      ),
    onSuccess: (_data, vars) => {
      if (trackingShop) {
        const source = resolveLeverSource({
          fromCancelFlowFlag: vars.fromCancelFlow,
          conversationId: vars.conversationId,
          offerTaken: vars.offerTaken,
        });
        trackBuyAgainWidgetInteract(
          "subscription_change_frequency",
          trackingShop,
          {
            productId: vars.productId,
            new_interval: vars.interval,
            interval_unit: vars.intervalUnit,
            previous_interval: vars.previousInterval,
            interval_diff:
              vars.previousInterval != null
                ? vars.interval - vars.previousInterval
                : null,
            ...leverActionTrackingProps({
              source,
              subscriptionContractId: vars.subscriptionId,
              orderCount: vars.orderCount,
              cancelAttemptId: vars.conversationId,
            }),
          },
        );
      }
      void queryClient.invalidateQueries({ queryKey: ["purchased-products"] });
      void queryClient.invalidateQueries({
        queryKey: ["purchased-product-detail", vars.productId],
      });
      setResult({
        actionType: "change_frequency",
        title: `Your deliveries are now every ${vars.interval} ${vars.interval === 1 ? vars.intervalUnit : `${vars.intervalUnit}s`
          }`,
      });
      // Change frequency can be launched from the cancel/retention flow too.
      setChangeFrequencyDialogOpen(false);
      setChangeFrequencyFromCancelFlow(false);
      setCancelDialogOpen(false);
    },
    onError: (error) => {
      toastSubscriptionApiError(error);
    },
  });

  const extendPauseMutation = useMutation({
    mutationFn: (vars: {
      subscriptionId: number;
      pausedUntil: string;
      productId: number;
      months: number;
    }) => extendPauseStayAiSubscription(vars.subscriptionId, vars.pausedUntil),
    onSuccess: (_data, vars) => {
      if (trackingShop) {
        trackBuyAgainWidgetInteract("subscription_extend_pause", trackingShop, {
          productId: vars.productId,
          subscription_contract_id: vars.subscriptionId,
          duration_months: vars.months,
        });
      }
      void queryClient.invalidateQueries({ queryKey: ["purchased-products"] });
      void queryClient.invalidateQueries({
        queryKey: ["purchased-product-detail", vars.productId],
      });
      setResult({
        actionType: "extend_pause",
        title: `Your subscription is now paused until ${formatSubscriptionDateForDisplay(
          vars.pausedUntil,
        )}`,
      });
      setExtendPauseDialogOpen(false);
    },
    onError: (error) => {
      toastSubscriptionApiError(error);
    },
  });

  const contactSupportMutation = useMutation({
    mutationFn: (vars: {
      subscriptionId: number;
      productId: number;
      message: string;
      productTitle?: string | null;
      customerEmail?: string | null;
      customerName?: string | null;
    }) =>
      contactSupportStayAiSubscription(vars.subscriptionId, vars.message, {
        productTitle: vars.productTitle,
        customerEmail: vars.customerEmail,
        customerName: vars.customerName,
      }),
    onSuccess: (_data, vars) => {
      if (trackingShop) {
        trackBuyAgainWidgetInteract(
          "subscription_contact_support",
          trackingShop,
          {
            productId: vars.productId,
            subscription_contract_id: vars.subscriptionId,
          },
        );
      }
      // Contact support shows its own plain success inside the dialog (no rating
      // prompt), so keep it open and don't route through the result dialog.
    },
    onError: (error) => {
      toastSubscriptionApiError(error);
    },
  });

  useEffect(() => {
    if (open) {
      pauseMutation.reset();
      unpauseMutation.reset();
      skipNextOrderMutation.reset();
      getOrderNowMutation.reset();
      cancelMutation.reset();
      undoScheduledMutation.reset();
      setNextBillingDateMutation.reset();
      extendPauseMutation.reset();
      changeFrequencyMutation.reset();
      contactSupportMutation.reset();
      setResult(null);
      setPauseDialogOpen(false);
      setCancelDialogOpen(false);
      setGetItNowDialogOpen(false);
      setCancelTodayDialogOpen(false);
      setSkipNextOrderDialogOpen(false);
      setUnpauseDialogOpen(false);
      setDelayDialogOpen(false);
      setChangeFrequencyDialogOpen(false);
      setExtendPauseDialogOpen(false);
      setContactSupportDialogOpen(false);
      setCancelDialogInitialStep("why-bought");
    } else {
      setResult(null);
      setPauseDialogOpen(false);
      setCancelDialogOpen(false);
      setGetItNowDialogOpen(false);
      setCancelTodayDialogOpen(false);
      setSkipNextOrderDialogOpen(false);
      setUnpauseDialogOpen(false);
      setDelayDialogOpen(false);
      setChangeFrequencyDialogOpen(false);
      setExtendPauseDialogOpen(false);
      setContactSupportDialogOpen(false);
      setCancelDialogInitialStep("why-bought");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on open/close only
  }, [open]);

  useEffect(() => {
    if (open && !product) {
      onOpenChangeRef.current(false);
    }
  }, [open, product]);

  useEffect(() => {
    if (!open) return;
    // Skip/pause deep links are blocked while a cancel is scheduled.
    const scheduled = !!product?.subscription_cancel_scheduled_at;
    // (BuyAgainPanel shows the unavailable dialog first; this is the race
    // fallback — close cleanly instead of leaving an invisible open sheet.)
    if ((isSkipOnly || isPauseOnly) && scheduled) onOpenChangeRef.current(false);
    else if (isGetItNowOnly) setGetItNowDialogOpen(true);
    else if (isSkipOnly) setSkipNextOrderDialogOpen(true);
    else if (isPauseOnly) setPauseDialogOpen(true);
  }, [open, isGetItNowOnly, isSkipOnly, isPauseOnly]);

  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" && window.innerWidth >= 768,
  );
  useEffect(() => {
    const fn = () => setIsDesktop(window.innerWidth >= 768);
    fn();
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  if (!product) return null;

  const lastChargeDate = product.subscription_last_charge_date ?? null;

  const allowedPauseMonths = getAllowedPauseMonths(lastChargeDate);
  const allowedExtendMonths = getAllowedExtendPauseMonths(
    product.subscription_next_billing_date,
    lastChargeDate,
  );
  const ageBasedMax = getAgeBasedMaxDate(lastChargeDate);
  const changeDeliveryDateBounds =
    getAgeBasedDeliveryDateBounds(lastChargeDate);
  const unpauseDeliveryDateBounds = {
    min: changeDeliveryDateBounds?.min ?? new Date().toISOString().slice(0, 10),
    max: ageBasedMax ?? pauseUntilMonthsFromToday(1),
  };

  const statusLower = product.subscription_status?.toLowerCase() ?? "";
  const isFlexible = product.subscription_is_flexible === true;
  const cancellationRequested =
    !!product.subscription_cancellation_requested_at;
  // A PENDING product swap awaiting support confirmation blocks cancel (only
  // cancel — delay / change-frequency / switch-to-one-time stay available).
  const swapPending = swapStatusQuery.data?.status === "PENDING";
  const swapPendingProducts = swapStatusQuery.data?.products ?? [];
  const cancelScheduled = isCancelScheduled(
    product.subscription_cancel_scheduled_at,
    statusLower,
  );
  const canPause0 =
    !isFlexible && statusLower === "active" && allowedPauseMonths.length > 0;
  const canUnpause = !isFlexible && statusLower === "paused";
  const canCancel0 =
    (statusLower === "active" || statusLower === "paused") &&
    !cancellationRequested &&
    !swapPending;
  const canSkipNextOrder0 =
    !isFlexible &&
    statusLower === "active" &&
    product.can_skip_next_order === true;
  const canGetItNow = statusLower === "active" || statusLower === "paused";
  const canDelay = !isFlexible && statusLower === "active" && getDelayPresets(product.subscription_next_billing_date, lastChargeDate).length > 0;
  const canExtendPause0 =
    !isFlexible && statusLower === "paused" && allowedExtendMonths.length > 0;
  const canChangeFrequency0 =
    !isFlexible &&
    (statusLower === "active" || statusLower === "paused") &&
    product.subscription_billing_interval_count != null &&
    product.subscription_billing_interval != null;
  // While a cancel is scheduled: hide every action that would dodge it.
  const {
    canPause,
    canCancel,
    canSkipNextOrder,
    canExtendPause,
    canChangeFrequency,
  } = applyScheduledGate(cancelScheduled, {
    canPause: canPause0,
    canCancel: canCancel0,
    canSkipNextOrder: canSkipNextOrder0,
    canExtendPause: canExtendPause0,
    canChangeFrequency: canChangeFrequency0,
  });
  const skipNextOrderShipDate =
    product.subscription_next_billing_date &&
      product.subscription_billing_interval &&
      product.subscription_billing_interval_count != null
      ? addBillingInterval(
        product.subscription_next_billing_date,
        product.subscription_billing_interval,
        product.subscription_billing_interval_count,
      )
      : null;
  // Same figure BeforeYouCancelBody displays as the price difference.
  const cancelFeeDiff =
    product.subscription_unit_price != null &&
    product.subscription_one_time_price != null &&
    Number.isFinite(product.subscription_unit_price) &&
    Number.isFinite(product.subscription_one_time_price)
      ? Math.max(
          0,
          product.subscription_one_time_price - product.subscription_unit_price,
        )
      : null;
  const isBeforeSecondShipment =
    (product.subscription_order_count == null ||
      product.subscription_order_count < 2) &&
    !product.subscription_conversion_fee_paid;

  const actionPending =
    pauseMutation.isPending ||
    unpauseMutation.isPending ||
    skipNextOrderMutation.isPending ||
    getOrderNowMutation.isPending ||
    cancelMutation.isPending ||
    undoScheduledMutation.isPending ||
    setNextBillingDateMutation.isPending ||
    extendPauseMutation.isPending ||
    changeFrequencyMutation.isPending ||
    contactSupportMutation.isPending;

  const submitPauseMonths = (months: number) => {
    const pauseDate = pauseUntilMonthsFromToday(months);
    pauseMutation.mutate({
      subscriptionId: product.shopify_subscription_id,
      pausedUntil: pauseDate,
      productId: product.id,
      months,
    });
  };

  const submitExtendPauseMonths = (months: number) => {
    const nextBilling = product.subscription_next_billing_date;
    if (!nextBilling) return;
    const newDate = extendPauseByMonths(nextBilling, months);
    extendPauseMutation.mutate({
      subscriptionId: product.shopify_subscription_id,
      pausedUntil: newDate,
      productId: product.id,
      months,
    });
  };

  // First three of these are shown by default; the rest collapse behind "More".
  // The footer appends Cancel, then renders the "other" action below it.
  const footerActions: SubscriptionFooterAction[] = [];
  if (canGetItNow) footerActions.push({ key: "get-it-now", label: "Get it now", onClick: () => setGetItNowDialogOpen(true), className: DARK_BRAND_BTN_CLASS });
  if (canDelay) footerActions.push({ key: "delay", label: "Delay", className: PRIMARY_BRAND_BTN_CLASS, onClick: () => { setNextBillingDateMutation.reset(); setDelayFromCancelFlow(false); setDelayDialogOpen(true); } });
  if (canPause) footerActions.push({ key: "pause", label: "Pause", onClick: () => setPauseDialogOpen(true) });
  else if (canUnpause) footerActions.push({ key: "unpause", label: "Unpause", onClick: () => setUnpauseDialogOpen(true), className: DARK_BRAND_BTN_CLASS });
  if (canSkipNextOrder) footerActions.push({ key: "skip-next-order", label: "Skip upcoming order", onClick: () => setSkipNextOrderDialogOpen(true) });
  if (canExtendPause) footerActions.push({ key: "extend-pause", label: "Extend pause", onClick: () => { extendPauseMutation.reset(); setExtendPauseDialogOpen(true); } });
  if (canChangeFrequency) footerActions.push({ key: "change-frequency", label: "Change frequency", onClick: () => { changeFrequencyMutation.reset(); setChangeFrequencyFromCancelFlow(false); setChangeFrequencyDialogOpen(true); } });
  footerActions.push({ key: "other", label: "Other", onClick: () => { contactSupportMutation.reset(); setContactSupportDialogOpen(true); } });

  return (
    <>
      {!isInitialActionOnly && (
        <Sheet open={open} onOpenChange={onOpenChange}>
          <SheetContent
            side={isDesktop ? "right" : "bottom"}
            // Mobile: full-screen modal. Pinning top AND bottom stretches the
            // popup to the viewport regardless of the base sheet's `h-auto`.
            className="tw:data-[side=bottom]:top-0 tw:flex tw:flex-col tw:gap-0 tw:overflow-hidden tw:z-60 tw:data-[side=bottom]:z-60 tw:data-[side=right]:z-60"
            overlayClassName="tw:z-55"
            onOpenChange={onOpenChange}
            showCloseButton={false}
          >
            <SheetHeader className="tw:shrink-0 tw:flex tw:flex-row tw:items-center tw:justify-between tw:pr-2 tw:pb-2">
              <SheetTitle
                className="tw:font-semibold tw:text-base tw:flex tw:items-center tw:gap-2"
                style={{ color: "var(--so-text, #523D33)" }}
              >
                ⚡️ Manage Subscription
              </SheetTitle>
              <SheetClose
                data-slot="sheet-close"
                render={
                  <Button
                    variant="ghost"
                    className="tw:p-0 tw:m-0"
                    size="icon-sm"
                  />
                }
              >
                <X />
              </SheetClose>
            </SheetHeader>

            <div className="tw:flex tw:flex-1 tw:flex-col tw:min-h-0 tw:gap-2">
              <div className="tw:flex-1 tw:overflow-y-auto tw:min-h-0 tw:px-4 tw:pt-2 tw:pb-2 tw:space-y-4">
                <ManageSubscriptionProductSummary
                  product={product}
                  products={products}
                />
                {cancelScheduled && (
                  <ScheduledCancelBanner
                    endDate={product.subscription_next_billing_date ?? null}
                    onKeep={() =>
                      undoScheduledMutation.mutate({
                        subscriptionId: product.shopify_subscription_id,
                        productId: product.id,
                      })
                    }
                    keepPending={undoScheduledMutation.isPending}
                  />
                )}
                {swapPending && (
                  <SwapPendingBanner products={swapPendingProducts} />
                )}
                <h3 className="tw:text-center tw:font-medium tw:text-base tw:pt-2">
                  What would you like to do?
                </h3>
              </div>

              <ManageSubscriptionSheetFooter
                actions={footerActions}
                canCancel={canCancel}
                onCancel={() => {
                  setChatFailed(false);
                  setCancelDialogOpen(true);
                }}
                cancellationRequested={cancellationRequested}
                swapPending={swapPending}
                cancelScheduled={cancelScheduled}
                onCancelToday={() => setCancelTodayDialogOpen(true)}
                cancelTodayLabel={cancelTodayLabel(cancelFeeDiff)}
                actionPending={actionPending}
              />
            </div>
          </SheetContent>
        </Sheet>
      )}

      <PauseSubscriptionDialog
        open={pauseDialogOpen}
        onOpenChange={(nextOpen) => {
          setPauseDialogOpen(nextOpen);
          if (!nextOpen && isPauseOnly) onOpenChangeRef.current(false);
        }}
        onDismiss={() => {
          setPauseDialogOpen(false);
          if (isPauseOnly) onOpenChangeRef.current(false);
        }}
        onConfirmPauseMonths={submitPauseMonths}
        allowedMonths={allowedPauseMonths}
        pauseIsPending={pauseMutation.isPending}
        defaultMonths={isPauseOnly ? 2 : undefined}
      />

      {cancelFlowVariant === "chat" && !chatFailed ? (
        <CancelSubscriptionChat
          open={cancelDialogOpen}
          onOpenChange={(nextOpen) => {
            setCancelDialogOpen(nextOpen);
            if (!nextOpen) {
              setInvoicePayment(null);
              const closeSheet = shouldCloseSheetAfterCancelFlow(
                cancelMutation.isSuccess,
                cancelMutation.variables?.cancelAction,
              );
              cancelMutation.reset();
              if (closeSheet) {
                onOpenChangeRef.current(false);
              }
            }
          }}
          product={product}
          isBeforeSecondShipment={isBeforeSecondShipment}
          isFlexibleSubscription={isFlexible}
          changeFrequencyAvailable={
            product.subscription_billing_interval_count != null &&
            product.subscription_billing_interval != null
          }
          trackingShop={trackingShop}
          customerEmail={customerEmail}
          customerName={customerName}
          siblingProductTitles={(products ?? [])
            .filter((p) => p.id !== product.id && p.title)
            .map((p) => p.title)}
          contractProducts={products ?? [product]}
          onSubmit={(data) =>
            cancelMutation.mutateAsync({
              subscriptionId: product.shopify_subscription_id,
              whyBought: data.whyBought,
              whyDetail: data.whyDetail,
              cancelReason: data.cancelReason,
              cancelReasonOther: data.cancelReasonOther,
              cancelReasonId: data.cancelReasonId,
              cancelAction: data.cancelAction,
              productId: product.id,
              orderCount: product.subscription_order_count ?? null,
              isBeforeSecondShipment,
              offerDeclined: data.offerDeclined,
              conversationId: data.conversationId,
              feeAmountWaived: data.feeAmountWaived,
            })
          }
          onDelayDate={(nextBillingDate, weeks, cancelContext) =>
            setNextBillingDateMutation.mutateAsync({
              subscriptionId: product.shopify_subscription_id,
              nextBillingDate,
              productId: product.id,
              weeks,
              fromCancelFlow: true,
              orderCount: product.subscription_order_count,
              conversationId: cancelContext?.conversationId,
              offerTaken: cancelContext?.offerTaken,
            })
          }
          onChangeFrequencyTo={(interval, intervalUnit, cancelContext) =>
            changeFrequencyMutation.mutateAsync({
              subscriptionId: product.shopify_subscription_id,
              interval,
              intervalUnit,
              productId: product.id,
              previousInterval:
                product.subscription_billing_interval_count ?? null,
              fromCancelFlow: true,
              orderCount: product.subscription_order_count,
              conversationId: cancelContext?.conversationId,
              offerTaken: cancelContext?.offerTaken,
            })
          }
          onFatalError={() => setChatFailed(true)}
          cancelAfterDeliveryEnabled={cancelAfterDeliveryEnabled}
        />
      ) : (
        <CancelSubscriptionDialog
          trackingShop={trackingShop}
          open={cancelDialogOpen}
          onOpenChange={(nextOpen) => {
            setCancelDialogOpen(nextOpen);
            if (!nextOpen) {
              setInvoicePayment(null);
              const closeSheet = shouldCloseSheetAfterCancelFlow(
                cancelMutation.isSuccess,
                cancelMutation.variables?.cancelAction,
              );
              cancelMutation.reset();
              if (closeSheet) {
                onOpenChangeRef.current(false);
              }
            }
          }}
          product={product}
          subscriptionOrderCount={product.subscription_order_count}
          isBeforeSecondShipment={isBeforeSecondShipment}
          isFlexibleSubscription={isFlexible}
          initialStep={cancelDialogInitialStep}
          cancelAfterDeliveryEnabled={cancelAfterDeliveryEnabled}
          onSubmit={(data) => {
            cancelMutation.mutate({
              subscriptionId: product.shopify_subscription_id,
              whyBought: data.whyBought,
              cancelReason: data.cancelReason,
              cancelReasonOther: data.cancelReasonOther,
              cancelReasonId: data.cancelReasonId,
              cancelAction: data.cancelAction,
              productId: product.id,
              orderCount: product.subscription_order_count ?? null,
              isBeforeSecondShipment,
              offerDeclined: data.offerDeclined,
              cancelAttemptId: data.cancelAttemptId,
              feeAmountWaived: cancelFeeDiff,
            });
          }}
          submitPending={cancelMutation.isPending}
          submitSuccess={cancelMutation.isSuccess}
          invoicePayment={invoicePayment}
          onDelay={() => { setNextBillingDateMutation.reset(); setDelayFromCancelFlow(true); setDelayDialogOpen(true); }}
          onChangeFrequency={
            product.subscription_billing_interval_count != null &&
              product.subscription_billing_interval != null
              ? () => {
                changeFrequencyMutation.reset();
                setChangeFrequencyFromCancelFlow(true);
                setChangeFrequencyDialogOpen(true);
              }
              : undefined
          }
        />
      )}

      <CancelTodayDialog
        open={cancelTodayDialogOpen && cancelScheduled}
        onOpenChange={(next) => {
          setCancelTodayDialogOpen(next);
          if (!next) {
            const wasSuccess = cancelMutation.isSuccess;
            cancelMutation.reset();
            if (wasSuccess) onOpenChangeRef.current(false);
          }
        }}
        amount={cancelFeeDiff ?? 0}
        onConfirm={() => {
          if (trackingShop) {
            trackBuyAgainWidgetInteract(
              "subscription_cancel_today_from_scheduled",
              trackingShop,
              {
                productId: product.id,
                subscription_contract_id: product.shopify_subscription_id,
              },
            );
          }
          cancelMutation.mutate({
            subscriptionId: product.shopify_subscription_id,
            whyBought: "",
            cancelReason: "Cancel today instead of after next delivery",
            cancelReasonId: "cancel_today_after_scheduled",
            cancelAction: "switch_to_one_time",
            productId: product.id,
            orderCount: product.subscription_order_count ?? null,
            isBeforeSecondShipment: true,
          });
        }}
        confirmPending={cancelMutation.isPending}
        confirmSuccess={cancelMutation.isSuccess}
        redirecting={!!cancelMutation.data?.invoice_url}
      />

      <GetItNowDialog
        open={getItNowDialogOpen}
        onOpenChange={(nextOpen) => {
          setGetItNowDialogOpen(nextOpen);
          if (!nextOpen && isGetItNowOnly) onOpenChangeRef.current(false);
        }}
        productTitle={product.title}
        onConfirm={() =>
          getOrderNowMutation.mutate({
            subscriptionId: product.shopify_subscription_id,
            productId: product.id,
          })
        }
        confirmPending={getOrderNowMutation.isPending}
      />

      <SkipNextOrderDialog
        open={skipNextOrderDialogOpen}
        onOpenChange={(nextOpen) => {
          setSkipNextOrderDialogOpen(nextOpen);
          if (!nextOpen && isSkipOnly) onOpenChangeRef.current(false);
        }}
        nextOrderDate={skipNextOrderShipDate}
        onConfirm={() =>
          skipNextOrderMutation.mutate({
            subscriptionId: product.shopify_subscription_id,
            productId: product.id,
            shipDate: skipNextOrderShipDate,
          })
        }
        confirmPending={skipNextOrderMutation.isPending}
      />

      <UnpauseSubscriptionDialog
        open={unpauseDialogOpen}
        onOpenChange={setUnpauseDialogOpen}
        productTitle={product.title}
        onConfirm={(nextBillingDate) =>
          unpauseMutation.mutate({
            subscriptionId: product.shopify_subscription_id,
            productId: product.id,
            nextBillingDate,
          })
        }
        confirmPending={unpauseMutation.isPending}
        deliveryDateBounds={unpauseDeliveryDateBounds}
      />

      <DelayDialog
        open={delayDialogOpen}
        onOpenChange={(nextOpen) => {
          setDelayDialogOpen(nextOpen);
          if (!nextOpen) setDelayFromCancelFlow(false);
        }}
        nextBillingDate={product.subscription_next_billing_date}
        lastChargeDate={lastChargeDate}
        onConfirm={(date, weeks) => {
          setNextBillingDateMutation.mutate({
            subscriptionId: product.shopify_subscription_id,
            nextBillingDate: date,
            productId: product.id,
            weeks,
            fromCancelFlow: delayFromCancelFlow,
            orderCount: product.subscription_order_count,
          });
        }}
        confirmPending={setNextBillingDateMutation.isPending}
        actionIsPrimary={delayFromCancelFlow}
      />

      <ChangeFrequencyDialog
        open={changeFrequencyDialogOpen}
        actionIsPrimary={changeFrequencyFromCancelFlow}
        onOpenChange={(nextOpen) => {
          setChangeFrequencyDialogOpen(nextOpen);
          if (!nextOpen) setChangeFrequencyFromCancelFlow(false);
        }}
        currentInterval={product.subscription_billing_interval_count ?? null}
        currentIntervalUnit={product.subscription_billing_interval ?? null}
        onConfirm={(interval, intervalUnit) =>
          changeFrequencyMutation.mutate({
            subscriptionId: product.shopify_subscription_id,
            interval,
            intervalUnit,
            productId: product.id,
            previousInterval: product.subscription_billing_interval_count ?? null,
            fromCancelFlow: changeFrequencyFromCancelFlow,
            orderCount: product.subscription_order_count,
          })
        }
        confirmPending={changeFrequencyMutation.isPending}
      />

      <ExtendPauseDialog
        open={extendPauseDialogOpen}
        onOpenChange={setExtendPauseDialogOpen}
        allowedMonths={allowedExtendMonths}
        currentNextBillingDate={product.subscription_next_billing_date ?? null}
        onConfirmExtendMonths={submitExtendPauseMonths}
        confirmPending={extendPauseMutation.isPending}
      />

      <ContactSupportDialog
        open={contactSupportDialogOpen}
        onOpenChange={(nextOpen) => {
          setContactSupportDialogOpen(nextOpen);
          if (!nextOpen) {
            const wasSuccess = contactSupportMutation.isSuccess;
            contactSupportMutation.reset();
            if (wasSuccess) onOpenChangeRef.current(false);
          }
        }}
        onSend={(message) =>
          contactSupportMutation.mutate({
            subscriptionId: product.shopify_subscription_id,
            productId: product.id,
            message,
            productTitle: product.title,
            customerEmail: customerEmail ?? null,
            customerName: customerName ?? null,
          })
        }
        sendPending={contactSupportMutation.isPending}
        sendSuccess={contactSupportMutation.isSuccess}
      />

      <ActionResultDialog
        open={result != null}
        onOpenChange={(next) => {
          if (!next) {
            setResult(null);
            // Re-fetch the list next time so a second action doesn't re-show
            // products the buyer just rated.
            void queryClient.invalidateQueries({
              queryKey: ["widget-products-to-rate"],
            });
            onOpenChangeRef.current(false);
          }
        }}
        actionType={result?.actionType ?? "pause"}
        title={result?.title ?? ""}
        message={result?.message}
        shop={trackingShop ?? null}
        shopifyCustomerId={shopifyCustomerId ?? null}
        productsToRate={productsToRateQuery.data}
        focusProductId={product?.id ?? null}
        customerEmail={customerEmail}
      />
    </>
  );
}
